import { IBulkEditService, ResourceTextEdit } from 'vscode/vscode/vs/editor/browser/services/bulkEditService';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { registerAction2, Action2, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { ActiveEditorContext } from 'vscode/vscode/vs/workbench/common/contextkeys';
import { SideBySideDiffElementViewModel } from './diffElementViewModel.js';
import { NOTEBOOK_DIFF_CELL_PROPERTY, NOTEBOOK_DIFF_CELL_PROPERTY_EXPANDED, NOTEBOOK_DIFF_CELL_INPUT } from './notebookDiffEditorBrowser.js';
import { NotebookTextDiffEditor } from './notebookDiffEditor.js';
import { openAsTextIcon, revertIcon, renderOutputIcon, previousChangeIcon, nextChangeIcon } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookIcons';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Extensions } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { DEFAULT_EDITOR_ASSOCIATION } from 'vscode/vscode/vs/workbench/common/editor';
import { NOTEBOOK_DIFF_EDITOR_ID } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';

const _moduleId = "vs/workbench/contrib/notebook/browser/diff/notebookDiffActions";
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'notebook.diff.switchToText',
            icon: openAsTextIcon,
            title: ( localize2WithPath(_moduleId, 0, 'Open Text Diff Editor')),
            precondition: ( (ActiveEditorContext.isEqualTo(NotebookTextDiffEditor.ID))),
            menu: [{
                    id: MenuId.EditorTitle,
                    group: 'navigation',
                    when: ( (ActiveEditorContext.isEqualTo(NotebookTextDiffEditor.ID)))
                }]
        });
    }
    async run(accessor) {
        const editorService = accessor.get(IEditorService);
        const activeEditor = editorService.activeEditorPane;
        if (activeEditor && activeEditor instanceof NotebookTextDiffEditor) {
            const diffEditorInput = activeEditor.input;
            await editorService.openEditor({
                original: { resource: diffEditorInput.original.resource },
                modified: { resource: diffEditorInput.resource },
                label: diffEditorInput.getName(),
                options: {
                    preserveFocus: false,
                    override: DEFAULT_EDITOR_ASSOCIATION.id
                }
            });
        }
    }
});
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'notebook.diff.cell.revertMetadata',
            title: ( localizeWithPath(_moduleId, 1, "Revert Metadata")),
            icon: revertIcon,
            f1: false,
            menu: {
                id: MenuId.NotebookDiffCellMetadataTitle,
                when: NOTEBOOK_DIFF_CELL_PROPERTY
            },
            precondition: NOTEBOOK_DIFF_CELL_PROPERTY
        });
    }
    run(accessor, context) {
        if (!context) {
            return;
        }
        const original = context.cell.original;
        const modified = context.cell.modified;
        if (!original || !modified) {
            return;
        }
        modified.textModel.metadata = original.metadata;
    }
});
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'notebook.diff.cell.switchOutputRenderingStyleToText',
            title: ( localizeWithPath(_moduleId, 2, "Switch Output Rendering")),
            icon: renderOutputIcon,
            f1: false,
            menu: {
                id: MenuId.NotebookDiffCellOutputsTitle,
                when: NOTEBOOK_DIFF_CELL_PROPERTY_EXPANDED
            }
        });
    }
    run(accessor, context) {
        if (!context) {
            return;
        }
        context.cell.renderOutput = !context.cell.renderOutput;
    }
});
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'notebook.diff.cell.revertOutputs',
            title: ( localizeWithPath(_moduleId, 3, "Revert Outputs")),
            icon: revertIcon,
            f1: false,
            menu: {
                id: MenuId.NotebookDiffCellOutputsTitle,
                when: NOTEBOOK_DIFF_CELL_PROPERTY
            },
            precondition: NOTEBOOK_DIFF_CELL_PROPERTY
        });
    }
    run(accessor, context) {
        if (!context) {
            return;
        }
        if (!(context.cell instanceof SideBySideDiffElementViewModel)) {
            return;
        }
        const original = context.cell.original;
        const modified = context.cell.modified;
        const modifiedCellIndex = context.cell.mainDocumentTextModel.cells.indexOf(modified.textModel);
        if (modifiedCellIndex === -1) {
            return;
        }
        context.cell.mainDocumentTextModel.applyEdits([{
                editType: 2 , index: modifiedCellIndex, outputs: original.outputs
            }], true, undefined, () => undefined, undefined, true);
    }
});
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'notebook.diff.cell.revertInput',
            title: ( localizeWithPath(_moduleId, 4, "Revert Input")),
            icon: revertIcon,
            f1: false,
            menu: {
                id: MenuId.NotebookDiffCellInputTitle,
                when: NOTEBOOK_DIFF_CELL_INPUT
            },
            precondition: NOTEBOOK_DIFF_CELL_INPUT
        });
    }
    run(accessor, context) {
        if (!context) {
            return;
        }
        const original = context.cell.original;
        const modified = context.cell.modified;
        if (!original || !modified) {
            return;
        }
        const bulkEditService = accessor.get(IBulkEditService);
        return bulkEditService.apply([
            ( (new ResourceTextEdit(
            modified.uri,
            { range: modified.textModel.getFullModelRange(), text: original.textModel.getValue() }
        ))),
        ], { quotableLabel: 'Revert Notebook Cell Content Change' });
    }
});
class ToggleRenderAction extends Action2 {
    constructor(id, title, precondition, toggled, order, toggleOutputs, toggleMetadata) {
        super({
            id: id,
            title,
            precondition: precondition,
            menu: [{
                    id: MenuId.EditorTitle,
                    group: 'notebook',
                    when: precondition,
                    order: order,
                }],
            toggled: toggled
        });
        this.toggleOutputs = toggleOutputs;
        this.toggleMetadata = toggleMetadata;
    }
    async run(accessor) {
        const configurationService = accessor.get(IConfigurationService);
        if (this.toggleOutputs !== undefined) {
            const oldValue = configurationService.getValue('notebook.diff.ignoreOutputs');
            configurationService.updateValue('notebook.diff.ignoreOutputs', !oldValue);
        }
        if (this.toggleMetadata !== undefined) {
            const oldValue = configurationService.getValue('notebook.diff.ignoreMetadata');
            configurationService.updateValue('notebook.diff.ignoreMetadata', !oldValue);
        }
    }
}
registerAction2(class extends ToggleRenderAction {
    constructor() {
        super('notebook.diff.showOutputs', ( localize2WithPath(_moduleId, 5, 'Show Outputs Differences')), ( (ActiveEditorContext.isEqualTo(NotebookTextDiffEditor.ID))), ( (ContextKeyExpr.notEquals('config.notebook.diff.ignoreOutputs', true))), 2, true, undefined);
    }
});
registerAction2(class extends ToggleRenderAction {
    constructor() {
        super('notebook.diff.showMetadata', ( localize2WithPath(_moduleId, 6, 'Show Metadata Differences')), ( (ActiveEditorContext.isEqualTo(NotebookTextDiffEditor.ID))), ( (ContextKeyExpr.notEquals('config.notebook.diff.ignoreMetadata', true))), 1, undefined, true);
    }
});
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'notebook.diff.action.previous',
            title: ( localizeWithPath(_moduleId, 7, "Show Previous Change")),
            icon: previousChangeIcon,
            f1: false,
            keybinding: {
                primary: 1024  | 512  | 61 ,
                weight: 200 ,
                when: ( (ActiveEditorContext.isEqualTo(NotebookTextDiffEditor.ID)))
            },
            menu: {
                id: MenuId.EditorTitle,
                group: 'navigation',
                when: ( (ActiveEditorContext.isEqualTo(NotebookTextDiffEditor.ID)))
            }
        });
    }
    run(accessor) {
        const editorService = accessor.get(IEditorService);
        if (editorService.activeEditorPane?.getId() !== NOTEBOOK_DIFF_EDITOR_ID) {
            return;
        }
        const editor = editorService.activeEditorPane.getControl();
        editor?.previousChange();
    }
});
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'notebook.diff.action.next',
            title: ( localizeWithPath(_moduleId, 8, "Show Next Change")),
            icon: nextChangeIcon,
            f1: false,
            keybinding: {
                primary: 512  | 61 ,
                weight: 200 ,
                when: ( (ActiveEditorContext.isEqualTo(NotebookTextDiffEditor.ID)))
            },
            menu: {
                id: MenuId.EditorTitle,
                group: 'navigation',
                when: ( (ActiveEditorContext.isEqualTo(NotebookTextDiffEditor.ID)))
            }
        });
    }
    run(accessor) {
        const editorService = accessor.get(IEditorService);
        if (editorService.activeEditorPane?.getId() !== NOTEBOOK_DIFF_EDITOR_ID) {
            return;
        }
        const editor = editorService.activeEditorPane.getControl();
        editor?.nextChange();
    }
});
( (Registry.as(Extensions.Configuration))).registerConfiguration({
    id: 'notebook',
    order: 100,
    type: 'object',
    'properties': {
        'notebook.diff.ignoreMetadata': {
            type: 'boolean',
            default: false,
            markdownDescription: ( localizeWithPath(_moduleId, 9, "Hide Metadata Differences"))
        },
        'notebook.diff.ignoreOutputs': {
            type: 'boolean',
            default: false,
            markdownDescription: ( localizeWithPath(_moduleId, 10, "Hide Outputs Differences"))
        },
    }
});
